import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';
import { Dialog } from 'primereact/dialog';
import { Field, Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import { DropdownComponent, InputTextComponent } from '../../../components/prime-formik';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { NotificationContext } from '../../../components/GrowlNotifications';

const i18nMessages = defineMessages({
  sendPaymentLink: {
    id: 'buy-credits.send-payment-link',
    defaultMessage: 'Send payment link',
  },
  fullName: {
    id: 'buy-credits.full-name',
    defaultMessage: 'Full name',
  },
  email: {
    id: 'buy-credits.email',
    defaultMessage: 'Email',
  },
  language: {
    id: 'attrs.locale',
    defaultMessage: 'Preferred language',
  },
  send: {
    id: 'buy-credits.send',
    defaultMessage: 'Send',
  },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
});

const SendPaymentLinkForm = ({
  sendPaymentLinkDialogVisible,
  hideForm,
  additionalInput,
  supportedLanguages,
  sendCreditLinkMutation,
}) => {
  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);

  const initialValues = {
    fullName: '',
    email: '',
    language: 'en-US',
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required(),
    email: Yup.string().required(),
    language: Yup.string().required(),
  });

  const sendPaymentLink = async ({ fullName, email, language }, resetForm) => {
    try {
      await sendCreditLinkMutation({
        variables: {
          input: {
            fullName,
            email,
            language,
            ...additionalInput,
          },
        },
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      resetForm();
      hideForm();
    }
  };

  return (
    <Dialog
      header={formatMessage(i18nMessages.sendPaymentLink)}
      visible={sendPaymentLinkDialogVisible}
      style={{ textAlign: 'center' }}
      onHide={hideForm}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          sendPaymentLink(values, resetForm);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="form-grid bigger-padding-bottom">
              <div>
                <div className="input-label">{formatMessage(i18nMessages.fullName)}</div>
                <div>
                  <Field id="fullName" name="fullName" component={InputTextComponent} />
                </div>
              </div>
              <div>
                <div className="input-label">{formatMessage(i18nMessages.email)}</div>
                <div>
                  <Field id="email" name="email" component={InputTextComponent} />
                </div>
              </div>
              <div>
                <div className="input-label">{formatMessage(i18nMessages.language)}</div>
                <div>
                  <Field
                    id="language"
                    name="language"
                    component={DropdownComponent}
                    options={supportedLanguages}
                    filter
                    filterBy="label,value"
                  />
                </div>
              </div>
            </div>
            <div className="button-grid">
              <Button type="submit" disabled={isSubmitting} label={formatMessage(i18nMessages.send)} />
              <Button type="button" label={formatMessage(i18nMessages.cancel)} onClick={hideForm} />
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

SendPaymentLinkForm.propTypes = {
  sendPaymentLinkDialogVisible: PropTypes.bool.isRequired,
  hideForm: PropTypes.func.isRequired,
  additionalInput: PropTypes.objectOf(PropTypes.any).isRequired,
  supportedLanguages: PropTypes.arrayOf(PropTypes.any).isRequired,
  sendCreditLinkMutation: PropTypes.func.isRequired,
};

export default SendPaymentLinkForm;
